/**
 * Company schedule configuration — sports, age brackets, skill levels, locations,
 * and members (for coach assignment). Used by the create/edit class form.
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import {
  fetchCompanyLocations,
  fetchCompanyMembers,
  createClass,
  updateClass,
  deleteClass,
  deleteEventSeries,
  deleteFutureEventsFromSeries,
  createEventSeries,
  updateEventSeries,
  updateSingleOccurrence,
  type CreateClassInput,
  type UpdateClassInput,
  type CreateEventSeriesInput,
  type UpdateEventSeriesInput,
} from '../../services/schedule';
import { useCurrentCompany } from '../useCurrentCompany';

export function useScheduleSettings() {
  const { data: companyId } = useCurrentCompany();

  const sports = useQuery({
    queryKey: ['company-sports', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('company_sports')
        .select('id, sport_name, is_primary')
        .eq('company_id', companyId)
        .order('sport_name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });

  const ageBrackets = useQuery({
    queryKey: ['company-age-brackets', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('company_age_brackets')
        .select('id, title, min_age, max_age, all_ages')
        .eq('company_id', companyId)
        .order('min_age', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });

  const skillLevels = useQuery({
    queryKey: ['company-skill-levels', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error } = await supabase
        .from('company_skill_levels')
        .select('id, title, rank')
        .eq('company_id', companyId)
        .order('rank', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });

  const locations = useQuery({
    queryKey: ['company-locations', companyId],
    queryFn: () => (companyId ? fetchCompanyLocations(companyId) : Promise.resolve([])),
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });

  const members = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => (companyId ? fetchCompanyMembers(companyId) : Promise.resolve([])),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    sports: sports.data || [],
    ageBrackets: ageBrackets.data || [],
    skillLevels: skillLevels.data || [],
    locations: locations.data || [],
    members: members.data || [],
    isLoading:
      sports.isLoading ||
      ageBrackets.isLoading ||
      skillLevels.isLoading ||
      locations.isLoading,
  };
}

export function useClassMutations() {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
    queryClient.invalidateQueries({ queryKey: ['friendly_calendar_events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar_events'] });
    queryClient.invalidateQueries({ queryKey: ['event_series'] });
  };

  const createMutation = useMutation({
    mutationFn: (input: Omit<CreateClassInput, 'company_id'>) => {
      if (!companyId) throw new Error('No company selected');
      return createClass({ ...input, company_id: companyId });
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ eventId, input }: { eventId: string; input: UpdateClassInput }) =>
      updateClass(eventId, input),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ eventId }: { eventId: string }) => deleteClass(eventId),
    onSuccess: invalidate,
  });

  const deleteSeriesMutation = useMutation({
    mutationFn: ({ seriesId }: { seriesId: string }) => deleteEventSeries(seriesId),
    onSuccess: invalidate,
  });

  const deleteFutureMutation = useMutation({
    mutationFn: ({ seriesId }: { seriesId: string }) =>
      deleteFutureEventsFromSeries(seriesId),
    onSuccess: invalidate,
  });

  const createSeriesMutation = useMutation({
    mutationFn: (input: Omit<CreateEventSeriesInput, 'company_id'>) => {
      if (!companyId) throw new Error('No company selected');
      return createEventSeries({ ...input, company_id: companyId });
    },
    onSuccess: invalidate,
  });

  const updateSeriesMutation = useMutation({
    mutationFn: ({ seriesId, input }: { seriesId: string; input: UpdateEventSeriesInput }) =>
      updateEventSeries(seriesId, input),
    onSuccess: invalidate,
  });

  const updateOccurrenceMutation = useMutation({
    mutationFn: (params: Parameters<typeof updateSingleOccurrence>[0]) =>
      updateSingleOccurrence(params),
    onSuccess: invalidate,
  });

  return {
    createClass: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    createError: createMutation.error as Error | null,
    updateClass: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error as Error | null,
    deleteClass: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,

    createEventSeries: createSeriesMutation.mutateAsync,
    isCreatingSeries: createSeriesMutation.isPending,
    updateEventSeries: updateSeriesMutation.mutateAsync,
    isUpdatingSeries: updateSeriesMutation.isPending,
    updateSingleOccurrence: updateOccurrenceMutation.mutateAsync,
    isUpdatingOccurrence: updateOccurrenceMutation.isPending,
    deleteEventSeries: deleteSeriesMutation.mutateAsync,
    isDeletingSeries: deleteSeriesMutation.isPending,
    deleteFutureEvents: deleteFutureMutation.mutateAsync,
    isDeletingFuture: deleteFutureMutation.isPending,
  };
}
